/**
 * O CARTÃO de perfil — a cara de uma pessoa, desenhada num sítio só.
 *
 * Existe porque o mesmo desenho é preciso em dois lugares que não se parecem
 * nada por dentro: o `UserCard` (um popover posicionado à mão, com portal e
 * acções por baixo) e a pré-visualização do editor de perfil (um bloco parado
 * dentro de um formulário). Sem esta peça no meio, o editor mostrava uma
 * aproximação do cartão — e uma aproximação é exactamente o que faz alguém
 * escolher uma cor, gravar, e descobrir que fica diferente do que viu.
 *
 * O que aqui se desenha é o perfil que noutro sítio se paga: banner, cor de
 * perfil e moldura do avatar. A regra de composição é sempre a mesma —
 *
 *   1. há banner? o banner é o topo;
 *   2. não há, mas há cor? a cor é o topo, com um degradê para o fundo do
 *      cartão (uma barra chapada de cor viva ao cimo de um cartão escuro lê-se
 *      como um erro de carregamento);
 *   3. não há nada? uma faixa da própria superfície, mais baixa, só para o
 *      avatar ter onde assentar.
 *
 * — e o avatar cavalga sempre a fronteira entre o topo e o corpo, que é o que
 * dá ao cartão um centro.
 */

import type { PublicUser } from "@greenvoice/shared";
import { avatarFrame, BANNER_POS_PADRAO } from "@greenvoice/shared";
import type { ReactNode } from "react";

import { cssUrl } from "../lib/cssUrl";
import { Avatar, STATUS_LABEL } from "./Avatar";

/** O suficiente para desenhar um perfil. Um objecto e não sete props soltas: é
 *  o mesmo `PublicUser` que já anda em toda a app, e a pré-visualização passa
 *  uma cópia com os campos por gravar. */
export interface ProfileCardProps {
  user: PublicUser;
  /** Alcunha do servidor, quando há — o cartão mostra o nome como ele aparece. */
  displayName?: string | null | undefined;
  /** Cor do cargo, já calculada por quem abriu. */
  color?: string | null | undefined;
  /** O que vai por baixo do perfil: acções no popover, nada na pré-visualização. */
  children?: ReactNode;
}

export function ProfileCard({ user, displayName, color, children }: ProfileCardProps) {
  const nome = displayName ?? user.displayName ?? user.username;
  const moldura = avatarFrame(user.avatarFrame);
  const cor = user.accentColor;

  return (
    <div className="profile">
      <div
        className={`profile__top${user.bannerUrl ? " profile__top--imagem" : ""}`}
        style={
          user.bannerUrl
            ? {
                backgroundImage: cssUrl(user.bannerUrl),
                // O enquadramento escolhido por quem pôs a capa. Sem isto o
                // corte é sempre ao meio, e uma foto de grupo fica sem cabeças.
                backgroundPositionY: `${user.bannerPos ?? BANNER_POS_PADRAO}%`,
              }
            : cor
              ? { background: `linear-gradient(160deg, ${cor}, color-mix(in srgb, ${cor} 25%, transparent))` }
              : undefined
        }
      >
        {/* A linha de baixo herda a cor escolhida mesmo quando há banner: é o
            fio que liga o retrato de outra pessoa à identidade desta. */}
        {cor ? <span className="profile__risco" style={{ background: cor }} /> : null}
      </div>

      <div className="profile__corpo">
        <span className="profile__avatar">
          <Avatar user={user} size={72} showStatus frame={moldura} />
        </span>

        <div className="profile__nomes">
          <strong style={color ? { color } : undefined}>{nome}</strong>
          <span className="profile__handle">@{user.username}</span>
        </div>

        <span className="profile__estado">{user.statusText ?? STATUS_LABEL[user.status]}</span>

        {user.bio ? <p className="profile__bio">{user.bio}</p> : null}
        {user.bot ? <span className="tag">BOT</span> : null}

        {children}
      </div>
    </div>
  );
}
